// hooks/api/staff.queries.ts
import { notifyError, notifySuccess } from "@/lib/notification";
import * as staffService from "@/services/staff.service";
import {
  StaffCreateType,
  StaffParamsType,
  StaffType,
} from "@/services/staff.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { departmentsKey } from "@/hooks/api/department.queries";

export const staffKey = {
  all: ["staff"] as const,
  lists: (filters: StaffParamsType) =>
    [...staffKey.all, "lists", filters] as const,
  detail: (id: string) => [...staffKey.all, "detail", id] as const,
};

export const useStaffQuery = (filters: StaffParamsType) =>
  useQuery({
    queryKey: staffKey.lists(filters),
    queryFn: () => staffService.fetchAllStaff(filters),
    placeholderData: (prev) => prev,
  });

export const useStaffByIdQuery = (id: string, enabled = true) =>
  useQuery({
    queryKey: staffKey.detail(id),
    queryFn: () => staffService.fetchStaffById(id),
    enabled: enabled && !!id,
  });

export const useCreateStaffMutation = (filters: StaffParamsType) => {
  const queryClient = useQueryClient();
  return useMutation<StaffType, AxiosError, StaffCreateType>({
    mutationFn: (data) => staffService.createStaff(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: staffKey.lists(filters) });
      queryClient.invalidateQueries({ queryKey: departmentsKey.all });
      notifySuccess("Staff member added successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to add staff member";
      notifyError(apiError);
    },
  });
};

export const useUpdateStaffMutation = (filters: StaffParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: StaffCreateType }) =>
      staffService.updateStaff(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: staffKey.lists(filters) });
      queryClient.invalidateQueries({
        queryKey: staffKey.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: departmentsKey.all });
      notifySuccess("Staff member updated successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to update staff member";
      notifyError(apiError);
    },
  });
};

export const useDeleteStaffMutation = (filters: StaffParamsType) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => staffService.deleteStaff(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: staffKey.lists(filters) });
      queryClient.removeQueries({ queryKey: staffKey.detail(id) });
      queryClient.invalidateQueries({ queryKey: departmentsKey.all });
      notifySuccess("Staff member deleted successfully");
    },
    onError: (error: any) => {
      const apiError =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to delete staff member";
      notifyError(apiError);
    },
  });
};
